import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router';

import { useUserStore } from '@/stores/userStore';
import {
  useDashboardContext,
  useDashboardStore,
  useDashboardRefresh,
} from '@/stores/dashboardStore';
import { useDisableAllNotifications } from '@/hooks/api';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// SCROLLABLE NOTIFICATION DIALOG
import ScrollDialog from '@/components/ui/dialogs/ScrollableDialog';
import LocationMatch from '@/utils/locationMatch';
import { useLocales } from '@/locales';
import LanguagePopover from '@/layouts/LanguagePopover';
import { ThemeToggle } from '@/theme/ThemeToggle';
import { useErrorDialogDispatch } from '@/stores/errorDialogStore';
import {
  Bell,
  ChevronDown,
  LogOut,
  RefreshCw,
  User,
  Settings,
} from 'lucide-react';

function Header(props) {
  const { navPanel } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const removeUser = useUserStore(state => state.removeUser);
  const errorDispatch = useErrorDialogDispatch();
  const { translate } = useLocales();

  // Detect if we're on client or admin routes
  const isClientRoute = location.pathname.startsWith('/app');

  // Always call all hooks (Rules of Hooks requirement)
  const dashboardData = useDashboardContext();
  const appViewUser = useUserStore(state => state.user);
  const setNotifications = useDashboardStore(state => state.setNotifications);
  const { handleRefreshDashboardState, refreshing } = useDashboardRefresh();

  // Get user and notifications from appropriate store based on route
  const user = isClientRoute ? appViewUser : dashboardData?.user;
  const notifications = isClientRoute ? [] : dashboardData?.notifications || [];

  const [toggleNotifications, setToggleNotifications] = useState(false);

  const handleRemoveUser = () => {
    removeUser();
    navigate('/');
  };

  const handleMyAccount = () => {
    if (isClientRoute) {
      navigate('/app/view-profile');
    } else {
      navigate('/dashboard/view-profile');
    }
  };

  const handleEditMyAccount = () => {
    if (isClientRoute) {
      navigate('/app/edit-profile');
    } else {
      navigate('/dashboard/edit-profile');
    }
  };

  // TIMER BELL | TIMER BELL | TIMER BELL | TIMER BELL
  const [bell, setBell] = useState(true);
  useEffect(() => {
    setBell(true);
    return function () {
      setTimeout(() => setBell(false), 1000);
    };
  }, [notifications]);

  // Disable all notifications mutation using React Query hook (admin only)
  const { mutate: disableNotifications } = useDisableAllNotifications({
    onSuccess: () => {
      setToggleNotifications(false);
      setNotifications([]);
    },
    onError: () => {
      errorDispatch({
        type: 'OPEN_ERROR_DIALOG',
        errorMsg: translate('systemError'),
      });
    },
  });

  const handleDisableAllNotifications = () => {
    if (user?.token) {
      disableNotifications({});
    }
  };

  const details = user?.details;
  const userName = isClientRoute
    ? details?.CN
    : `${details?.firstName || ''} ${details?.lastName || ''}`.trim();
  const userInitials = isClientRoute
    ? details?.CN?.charAt(0) || 'U'
    : details?.firstName?.charAt(0) || 'U';
  return (
    <>
      <header
        className="fixed top-0 right-0 z-50 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        style={{ left: '280px' }}
      >
        <div className="flex h-full items-center justify-between px-6">
          <div />

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setToggleNotifications(true)}
              className="relative h-9 w-9"
            >
              <Bell
                className={`h-5 w-5 ${
                  bell && notifications.length > 0 ? 'text-orange-500' : ''
                }`}
              />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-[10px] font-medium text-white">
                  {notifications.length}
                </span>
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefreshDashboardState}
              disabled={refreshing}
              className="h-9 w-9"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  refreshing ? 'animate-spin text-orange-500' : ''
                }`}
              />
            </Button>

            <ThemeToggle />

            <LanguagePopover />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-9 w-9 rounded-full p-0"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarImage
                      src={details?.photo?.url}
                      alt={details?.photo?.filename || details?.photo?.name}
                    />
                    <AvatarFallback className="bg-orange-100 text-orange-700">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center gap-2 p-2">
                  <Avatar className="h-9 w-9">
                    <AvatarImage
                      src={details?.photo?.url}
                      alt={details?.photo?.filename || details?.photo?.name}
                    />
                    <AvatarFallback className="bg-orange-100 text-orange-700">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {userName || 'User'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {isClientRoute
                        ? translate('Client')
                        : user?.role || 'Admin'}
                    </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleMyAccount}>
                  <User className="mr-2 h-4 w-4" />
                  {translate('My_account')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEditMyAccount}>
                  <Settings className="mr-2 h-4 w-4" />
                  {translate('Settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleRemoveUser}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {translate('Logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {navPanel && <NavPanel />}

      {toggleNotifications && (
        <ScrollDialog
          open={toggleNotifications}
          handleClose={() => setToggleNotifications(false)}
          notifications={notifications}
          handleDisableAllNotifications={handleDisableAllNotifications}
        />
      )}
    </>
  );
}

Header.propTypes = {
  navPanel: PropTypes.bool,
};

export default Header;

function NavPanel() {
  const navigate = useNavigate();
  const { translate } = useLocales();
  const navPanelItems = [
    { label: translate('All_applications'), path: 'all-applications' },
  ];

  return (
    <div
      className="fixed top-16 right-0 z-40 border-b bg-background"
      style={{ left: '280px' }}
    >
      <div className="flex h-12 items-center justify-between px-6">
        <div className="flex items-center gap-1">
          {navPanelItems.map((item, index) => {
            const match = LocationMatch(`/dashboard/${item.path}`, true);
            return (
              <Button
                key={index}
                variant={match ? 'default' : 'ghost'}
                size="sm"
                onClick={() => navigate(`/dashboard/${item.path}`)}
                className={match ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                {item.label}
              </Button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {translate('Yet')}
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
